import { TextArea } from '@servicecompressionllc/sc-design-system';

interface Props {
  value: string;
  onChange: (next: string) => void;
  /** Frozen while a save is in flight: the page returns to view mode on
   *  success, so keystrokes typed during the round-trip would be discarded. */
  disabled?: boolean;
  /** Server-side cap on the field. Shows a counter; over it, the count reads
   *  as an error and the caller is expected to block save. */
  maxChars?: number;
  id?: string;
}

const PLACEHOLDER =
  'Raw markdown. Write it the way a mechanic would ask it.\n\n' +
  'For fault-code maps, one code per line works best:\n' +
  'SPN 168 FMI 17 — Battery voltage is low — check charging system…';

/**
 * Raw markdown editor — ricky-curator's General Knowledge editor, ported. A
 * plain textarea on purpose: we store markdown, and a WYSIWYG surface escapes
 * pasted markdown (`\# heading`) so content arrives looking right and saves
 * wrong. Rendering is the VIEW side's job; the pages show `MarkdownViewer`
 * when not editing.
 *
 * Replaced MDXEditor, whose source mode ships a light-theme CodeMirror that
 * ignores our tokens and painted a white block inside the dark page. Curator
 * never hit that: it uses MDXEditor with no source toggle at all.
 */
export const MarkdownEditor = ({ value, onChange, disabled, maxChars, id }: Props) => {
  const over = maxChars !== undefined && value.length > maxChars;
  return (
    <div className="space-y-1">
      <TextArea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        error={over}
        rows={24}
        aria-label="Markdown editor"
        spellCheck={false}
        placeholder={PLACEHOLDER}
        className="w-full font-mono text-xs leading-5"
      />
      {maxChars !== undefined && (
        // No maxLength on the textarea: a hard cap silently swallows the tail
        // of a paste, which is how content goes missing without anyone knowing.
        // Show the overage and let the page refuse to save instead.
        <p className={`text-right text-xs ${over ? 'text-danger' : 'text-ink-subtle'}`}>
          {value.length.toLocaleString()} / {maxChars.toLocaleString()} characters
          {over && ' — too long to save'}
        </p>
      )}
    </div>
  );
};
